<template>
  <div>
    <!-- Language Row -->
    <div class="wd-flex wd-items-center wd-px-5 wd-py-3 wd-bg-white wd-cursor-pointer" @click="isLanguageExpanded = !isLanguageExpanded">
      <funnow-icon max-width="24" height="24" image-path="language-icon_black.svg" class="mr-3" />
      <span class="wd-flex-1 wd-text-omd wd-font-normal wd-text-black50">{{ selectedLanguageText }}</span>
      <funnow-icon max-width="20" height="20" :image-path="`chevron-${isLanguageExpanded ? 'up' : 'down'}.svg`" />
    </div>
    <v-expand-transition>
      <v-list v-if="isLanguageExpanded" v-model="language" color="orange50" class="wd-py-0">
        <v-list-item
          v-for="lang in languageList"
          :key="lang.code"
          class="wd-px-5"
          :value="lang.code"
          :active="lang.active"
          @click="changeLanguage(lang)"
        >
          <v-list-item-title>{{ lang.text }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-expand-transition>

    <!-- Download App Button -->
    <div v-if="!isFunBookV2" class="wd-px-5 wd-py-3 wd-bg-white">
      <v-btn variant="outlined" class="wd-w-full wd-border wd-border-solid wd-border-green30 wd-rounded" @click="downloadApp">
        <funnow-icon max-width="24" height="24" image-path="gift--download.svg" class="mr-2" />
        <span class="wd-text-olg wd-font-normal wd-text-black50">{{ $t('download_app_with_promo') }}</span>
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useDownloadLinks } from '@/composition/useConst'
import FunnowIcon from '@/components/icons/funnow-icon.vue'
import { useVuetify } from '@/composition/useUtil'
import { usePlatformStore } from '@/store-pinia/platform'

type LocaleItem = { code: string; text: string }

const emit = defineEmits(['language-changed'])

const { $store, $config } = useNuxtApp()
const { isFunBookV2 } = storeToRefs(usePlatformStore())
const vuetify = useVuetify()
const { locale, locales, setLocale } = useI18n()
const { iosLink, androidLink } = useDownloadLinks()
const { loadLocaleMessages } = useLocaleMessages()

const isLanguageExpanded = ref(false)
const language = ref(locale.value)

const selectedLanguageText = computed(() => {
  const currLocale = (locales.value as LocaleItem[])?.find(l => l.code === locale.value)
  return currLocale?.text ?? ''
})

const languageList = computed(() => {
  return (locales.value as LocaleItem[])?.map(l => ({ ...l, active: l.code === locale.value })) ?? []
})

async function changeLanguage(param: LocaleItem) {
  const { code } = param
  await loadLocaleMessages(code)
  vuetify.locale.current.value = code
  await setLocale(code as any)
  $store.dispatch('region/getAllRegion')
  emit('language-changed')
}

function getMobileOS() {
  const ua = navigator.userAgent
  if (/android/i.test(ua)) {
    return 'Android'
  } else if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'iOS'
  }
  return 'Other'
}

function downloadApp() {
  const appOS = getMobileOS()
  let link = `${$config.public.BASE_URL}/app`
  if (appOS === 'iOS') {
    link = iosLink.value
  } else if (appOS === 'Android') {
    link = androidLink.value
  }
  window.open(link, '_blank')
}
</script>
